import Guid from "@/scripts/guid"
import { get, set, cloneDeep } from "lodash"
import { createDefaultInsight, InsightDetails } from "."
import { getCalculationAsCaseColumn, getCalculationTotalAsCaseColumn } from "@/scripts/insights"

export interface InsightDuplicationOption {
	id: string
    title: string
    propertyPaths: string[]
}

export const insightDuplicationOptions: InsightDuplicationOption[] = [
	{
		id: "folder",
		title: "Folder",
		propertyPaths: ["folder"]
	},
	{
		id: "displayColumns",
		title: "Columns",
		propertyPaths: ["displayColumns", "columns", "calculationColumns"]
	},
	{
		id: "defaultView",
		title: "Default View",
		propertyPaths: ["defaultView"]
	},
	{
		id: "filtering",
		title: "Filters",
		propertyPaths: ["clauses"]
	},
	{
		id: "grouping",
		title: "Grouping",
		propertyPaths: [
			"groupBy",
			"groupByOption",
			"groupByDataType",
			"groupByLabel",
			"groupByDateFormat",
			"groupingOptions",
			"showCalculatedDateRange",
			"showCalculatedUserStatus",
			"groupByApplyFilters"]
	},
	{
		id: "sorting",
		title: "Sorting",
		propertyPaths: ["sortBy", "sortByLabel", "sortByDataType", "sortByDescending"]
	},
	{
		id: "parameters",
		title: "Parameters",
		propertyPaths: ["dynamicParameters", "includeGlobalParameters"]
	},
	{
		id: "calculations",
		title: "Calculations",
		propertyPaths: ["calculations", "calculationTotals", "includeCalculationsFrom"]
	},
	{
		id: "forecasting",
		title: "Forecasting",
		propertyPaths: ["includeForecast", "forecastBy"]
	},
	{
		id: "formatting",
		title: "Formatting",
		propertyPaths: ["formattingOptions"]
	},
	{
		id: "preset",
		title: "Preset",
		propertyPaths: ["presetKey", "presetParameters"]
	}
]

export const duplicateInsight = (insight: InsightDetails, optionIdsToDefault: string[]): InsightDetails => {
	const optionsToDefault = insightDuplicationOptions.filter(o => optionIdsToDefault.includes(o.id))
	const duplicatedInsight = cloneDeep(insight) as InsightDetails
	duplicatedInsight.id = undefined
	duplicatedInsight.title = `${duplicatedInsight.title} [Duplicate]`
	duplicatedInsight.lastUpdated = undefined

	// Assign clauses new Keys
	duplicatedInsight.clauses.forEach(clause => {
		const key = Guid.newGuid()
		clause.id = key
		clause.key = key
	})

	let calculationFilterClauses = duplicatedInsight.clauses.filter(c => (c.field && c.field.startsWith("calculation")) || (c.orClause && c.subClauses && c.subClauses.some(s => s.field?.startsWith("calculation"))))
	// Assign calculations new Keys
	duplicatedInsight.calculations.forEach(calculation => {
		let newKey = Guid.newGuid()

		// Check added and subtracted lists for old calculation key and update to new key
		duplicatedInsight.calculationTotals!.forEach(calculationTotal => {
			if (calculationTotal.calculationList) {
				let addedCalcIndex = calculationTotal.calculationList.findIndex(key => key === calculation.key)
				if (addedCalcIndex !== -1 && calculationTotal.calculationList[addedCalcIndex]) {
					calculationTotal.calculationList[addedCalcIndex] = newKey
				}
			}

			if (calculationTotal.subtractCalculationList) {
				let subtractCalcIndex = calculationTotal.subtractCalculationList.findIndex(key => key === calculation.key)
				if (subtractCalcIndex !== -1 && calculationTotal.subtractCalculationList[subtractCalcIndex]) {
					calculationTotal.subtractCalculationList[subtractCalcIndex] = newKey
				}
			}

			if (calculationTotal.expressionCalculationList) {
				let expressionCalcIndex = calculationTotal.expressionCalculationList.findIndex(key => key === calculation.key)
				if (expressionCalcIndex !== -1 && calculationTotal.expressionCalculationList[expressionCalcIndex]) {
					let oldKey = calculationTotal.expressionCalculationList[expressionCalcIndex]
					calculationTotal.expressionCalculationList[expressionCalcIndex] = newKey
					calculationTotal.expression = calculationTotal.expression?.replace(new RegExp(oldKey, "g"), newKey) ?? null
				}
			}
		})

		// Update calculation filters
		if (!optionIdsToDefault.includes("calculations") && !optionIdsToDefault.includes("filters")) {
			let clausesToUpdate = calculationFilterClauses.filter(c => !c.orClause && c.field && c.field === "calculation." + calculation.key).map(c => c.id) || []
			let orClausesToUpdate = calculationFilterClauses.filter(c => c.orClause && c.subClauses && c.subClauses?.some(s => s.field === "calculation." + calculation.key)).map(c => c.id) || []
			duplicatedInsight.clauses.forEach(c => {
				if (clausesToUpdate.includes(c.id)) {
					c.fieldData = getCalculationAsCaseColumn(newKey, c.label)
					c.field = c.fieldData.key
				} else if (orClausesToUpdate.includes(c.id) && c.subClauses) {
					c.subClauses.forEach(s => {
						if (s.field && s.field === "calculation." + calculation.key) {
							s.fieldData = getCalculationAsCaseColumn(newKey, c.label)
							s.field = s.fieldData.key
						}
					})
				}
			})
		} else {
			duplicatedInsight.clauses.forEach(c => {
				if (c.orClause && c.subClauses) {
					c.subClauses.removeWhere(s => s.field === "calculation." + calculation.key)
				}
			})
			duplicatedInsight.clauses.removeWhere(c => calculationFilterClauses.some(filterClause => filterClause.id === c.id && filterClause.field && filterClause.field === "calculation." + calculation.key))
			duplicatedInsight.clauses.removeWhere(c => !!c.orClause && !!c.subClauses && c.subClauses.length === 0)
		}
		calculation.key = newKey
	})

	duplicatedInsight.calculationTotals!.forEach(calculationTotal => {
		let newKey = Guid.newGuid()

		if (!optionIdsToDefault.includes("calculations") && !optionIdsToDefault.includes("filters")) {
			// Update calculation total filters
			let clausesToUpdate = calculationFilterClauses.filter(c => !c.orClause && c.field && c.field === "calculationTotal." + calculationTotal.key).map(c => c.id) || []
			let orClausesToUpdate = calculationFilterClauses.filter(c => c.orClause && c.subClauses && c.subClauses?.some(s => s.field === "calculationTotal." + calculationTotal.key)).map(c => c.id) || []
			duplicatedInsight.clauses.forEach(c => {
				if (clausesToUpdate.includes(c.id)) {
					c.fieldData = getCalculationTotalAsCaseColumn(newKey, c.label)
					c.field = c.fieldData.key
				} else if (orClausesToUpdate.includes(c.id) && c.subClauses) {
					c.subClauses.forEach(s => {
						if (s.field && s.field === "calculationTotal." + calculationTotal.key) {
							s.fieldData = getCalculationTotalAsCaseColumn(newKey, c.label)
							s.field = s.fieldData.key
						}
					})
				}
			})
		} else {
			duplicatedInsight.clauses.forEach(c => {
				if (c.orClause && c.subClauses) {
					c.subClauses.removeWhere(s => s.field === "calculationTotal." + calculationTotal.key)
				}
			})
			duplicatedInsight.clauses.removeWhere(c => calculationFilterClauses.some(filterClause => filterClause.id === c.id && filterClause.field && filterClause.field === "calculationTotal." + calculationTotal.key))
			duplicatedInsight.clauses.removeWhere(c => !!c.orClause && !!c.subClauses && c.subClauses.length === 0)
		}
		calculationTotal.key = newKey
	})
	if (optionsToDefault.length === 0) {
		return duplicatedInsight
	}

	const defaultInsight = createDefaultInsight()
	for (let optionToDefault of optionsToDefault) {
		// Remove clauses that reference calculation columns when columns are not being duplicated
		if (optionToDefault.id === "displayColumns") {
			const calculationColumnKeys = duplicatedInsight.calculationColumns.map(col => col.key)
			duplicatedInsight.clauses = duplicatedInsight.clauses.filter(clause => {
				if (calculationColumnKeys.includes(clause.field!)) {
					return false
				}

				if (clause.orClause && clause.subClauses) {
					clause.subClauses = clause.subClauses.filter(subClause => !calculationColumnKeys.includes(subClause.field!))
					if (clause.subClauses.length === 0) {
						return false
					}
				}
				return true
			})
		}

		for (let propertyPath of optionToDefault.propertyPaths) {
			const defaultValue = get(defaultInsight, propertyPath)
			set(duplicatedInsight, propertyPath, defaultValue)
		}
	}

	return duplicatedInsight
}
